import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Plus, Edit2, Trash2, MapPin, Save, Loader2, Users, Image, Video, Upload, X, Link } from 'lucide-react';
import { API } from '@/App';
import RichTextEditor from '@/components/RichTextEditor';

const EVENT_TYPES = [
  { value: 'conference', label: 'Conference' },
  { value: 'webinar', label: 'Webinaire' },
  { value: 'workshop', label: 'Atelier pratique' },
  { value: 'congress', label: 'Congrès' },
  { value: 'formation', label: 'Formation' },
  { value: 'reunion', label: 'Reunion' },
];

const CoverUpload = ({ value, onChange }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/cms-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      onChange(res.data.url);
      toast.success('Image importee');
    } catch { toast.error("Erreur d'upload"); }
    setUploading(false);
  };

  return (
    <div>
      <Label className="mb-1.5 block">Image de couverture</Label>
      {value ? (
        <div className="relative rounded-xl overflow-hidden h-40 bg-gray-100">
          <img src={value.startsWith('/') ? `${API.replace('/api', '')}${value}` : value} alt="Cover" className="w-full h-full object-cover" />
          <button onClick={() => onChange('')} className="absolute top-2 right-2 bg-black/60 text-white p-1 rounded-lg hover:bg-black/80"><X size={14} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="w-full h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#35AFE4] hover:bg-blue-50/30 transition-all" data-testid="cover-upload-btn">
          {uploading ? <Loader2 size={20} className="animate-spin text-gray-400" /> : (
            <><Upload size={20} className="text-gray-400" /><span className="text-xs text-gray-500">Cliquez pour importer une image</span></>
          )}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
};

const GalleryManager = ({ eventId, photos, videos, recapVideoUrl, onUpdate }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [recapUrl, setRecapUrl] = useState(recapVideoUrl || '');
  const [savingRecap, setSavingRecap] = useState(false);
  const token = localStorage.getItem('token');

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    try {
      const res = await axios.post(`${API}/upload/cms-images-bulk`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      const newPhotos = [...(photos || []), ...res.data.map(r => r.url)];
      await axios.put(`${API}/events/${eventId}/gallery`, { gallery_photos: newPhotos }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(newPhotos, videos, recapUrl);
      toast.success(`${res.data.length} photo(s) importee(s)`);
    } catch { toast.error("Erreur d'upload"); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removePhoto = async (idx) => {
    const newPhotos = photos.filter((_, i) => i !== idx);
    await axios.put(`${API}/events/${eventId}/gallery`, { gallery_photos: newPhotos }, { headers: { Authorization: `Bearer ${token}` } });
    onUpdate(newPhotos, videos, recapUrl);
  };

  const addVideo = async () => {
    if (!videoUrl.trim()) return;
    const newVideos = [...(videos || []), videoUrl.trim()];
    await axios.put(`${API}/events/${eventId}/gallery`, { gallery_videos: newVideos }, { headers: { Authorization: `Bearer ${token}` } });
    onUpdate(photos, newVideos, recapUrl);
    setVideoUrl('');
    toast.success('Video ajoutee');
  };

  const removeVideo = async (idx) => {
    const newVideos = videos.filter((_, i) => i !== idx);
    await axios.put(`${API}/events/${eventId}/gallery`, { gallery_videos: newVideos }, { headers: { Authorization: `Bearer ${token}` } });
    onUpdate(photos, newVideos, recapUrl);
  };

  const saveRecapVideo = async () => {
    setSavingRecap(true);
    try {
      await axios.put(`${API}/events/${eventId}/gallery/video`, { recap_video_url: recapUrl.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(photos, videos, recapUrl.trim());
      toast.success(recapUrl.trim() ? 'Video resume enregistree' : 'Video resume supprimee');
    } catch { toast.error('Erreur'); }
    setSavingRecap(false);
  };

  return (
    <div className="space-y-4 pt-4 border-t border-gray-100">
      {/* Recap video URL */}
      <div>
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><Video size={15} /> Video resume (YouTube)</h3>
        <div className="flex gap-2">
          <Input value={recapUrl} onChange={e => setRecapUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="flex-1 text-sm" data-testid="recap-video-input" />
          <Button type="button" onClick={saveRecapVideo} disabled={savingRecap} variant="outline" size="sm" data-testid="save-recap-btn">
            {savingRecap ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </Button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2"><Image size={15} /> Photos ({(photos || []).length})</h3>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#35AFE4] hover:text-[#1e3a5f]" data-testid="gallery-upload-btn">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Importer des photos
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleBulkUpload} />
        {(photos || []).length > 0 ? (
          <div className="grid grid-cols-4 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden h-20 bg-gray-100 group">
                <img src={p.startsWith('/') ? `${API.replace('/api', '')}${p}` : p} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/60 text-white p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-gray-400 italic">Aucune photo. Importez des images pour la galerie.</p>}
      </div>

      <div>
        <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2 mb-3"><Video size={15} /> Videos ({(videos || []).length})</h3>
        <div className="flex gap-2 mb-2">
          <Input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className="flex-1 text-sm" data-testid="video-url-input" />
          <Button type="button" onClick={addVideo} variant="outline" size="sm"><Plus size={14} /> Ajouter</Button>
        </div>
        {(videos || []).length > 0 && (
          <div className="space-y-1.5">
            {videos.map((v, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-xs">
                <Link size={12} className="text-gray-400 flex-shrink-0" />
                <span className="flex-1 truncate text-gray-600">{v}</span>
                <button onClick={() => removeVideo(i)} className="text-gray-400 hover:text-red-500"><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EventsManagementPage = () => {
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [galleryEventId, setGalleryEventId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', start_date: '', end_date: '',
    location: '', location_type: 'physical', online_link: '',
    event_type: 'conference', max_participants: '',
    registration_open: true, published: true, poster_image: '',
  });

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, { params: { published_only: false } });
      setEvents(res.data);
    } catch { toast.error('Erreur lors du chargement'); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      title: '', description: '', start_date: '', end_date: '',
      location: '', location_type: 'physical', online_link: '',
      event_type: 'conference', max_participants: '',
      registration_open: true, published: true, poster_image: '',
    });
    setEditingEvent(null);
  };

  const handleEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      title: ev.title || '', description: ev.description || '',
      start_date: ev.start_date?.slice(0, 10) || '', end_date: ev.end_date?.slice(0, 10) || '',
      location: ev.location || '', location_type: ev.location_type || 'physical',
      online_link: ev.online_link || '', event_type: ev.event_type || 'conference',
      max_participants: ev.max_participants || '',
      registration_open: ev.registration_open !== false, published: ev.published !== false,
      poster_image: ev.poster_image || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      max_participants: form.max_participants ? parseInt(form.max_participants) : null,
      end_date: form.end_date || null,
      online_link: form.online_link || null,
    };
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success('Événement mis a jour');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Événement cree');
      }
      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer "${title}" ?`)) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success('Événement supprime');
      fetchEvents();
    } catch { toast.error('Erreur'); }
  };

  const typeLabel = (v) => EVENT_TYPES.find(t => t.value === v)?.label || v;

  const galleryEvent = events.find(e => e.id === galleryEventId);

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="events-management">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center"><Calendar size={20} className="text-white" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Événements</h1>
              <p className="text-xs text-gray-500">Gerez les événements, congres et formations</p>
            </div>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="create-event-btn">
            <Plus size={16} className="mr-2" /> Nouvel événement
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{events.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">A venir</p>
            <p className="text-2xl font-bold text-gray-900">{events.filter(e => new Date(e.start_date) > new Date()).length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-1">Inscriptions ouvertes</p>
            <p className="text-2xl font-bold text-gray-900">{events.filter(e => e.registration_open).length}</p>
          </div>
        </div>

        {/* Gallery info banner */}
        <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-100 rounded-2xl mb-6">
          <Image size={20} className="text-violet-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-bold text-violet-900">Galeries photos & vidéos des événements passés</p>
            <p className="text-violet-700 mt-0.5 text-xs leading-relaxed">
              Pour ajouter / supprimer les photos d'un événement passé, ou pour ajouter une vidéo récap YouTube, cliquez sur le bouton violet <strong className="font-semibold">"Gérer la galerie photos / vidéos"</strong> sur la carte de l'événement concerné. Les galeries sont accessibles uniquement aux membres pro connectés.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-400">Aucun événement.</p>
            </div>
          ) : events.map((ev, i) => (
            <div key={ev.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group" data-testid={`event-card-${i}`}>
              {ev.poster_image && (
                <div className="h-32 overflow-hidden">
                  <img src={ev.poster_image.startsWith('/') ? `${API.replace('/api', '')}${ev.poster_image}` : ev.poster_image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge className="bg-cyan-50 text-cyan-700 border-0 text-xs font-semibold">{typeLabel(ev.event_type)}</Badge>
                  <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleEdit(ev)} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600" data-testid={`edit-event-${i}`}><Edit2 size={13} /></button>
                    <button onClick={() => setGalleryEventId(ev.id)} className="p-1.5 rounded-lg hover:bg-purple-50 text-gray-400 hover:text-purple-600" title="Galerie"><Image size={13} /></button>
                    <button onClick={() => handleDelete(ev.id, ev.title)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600" data-testid={`delete-event-${i}`}><Trash2 size={13} /></button>
                  </div>
                </div>
                <h3 className="font-bold text-sm text-gray-900 mb-1 line-clamp-2">{ev.title}</h3>
                <div className="space-y-1 text-xs text-gray-600 mt-2">
                  <div className="flex items-center gap-2"><Calendar size={12} className="text-[#35AFE4]" /><span>{ev.start_date ? new Date(ev.start_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</span></div>
                  {ev.location && <div className="flex items-center gap-2"><MapPin size={12} className="text-[#35AFE4]" /><span className="line-clamp-1">{ev.location}</span></div>}
                </div>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-50 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ev.published ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {ev.published ? 'Publié' : 'Brouillon'}
                  </span>
                  {(ev.gallery_photos || []).length > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700">{ev.gallery_photos.length} photos</span>}
                  {(ev.gallery_videos || []).length > 0 && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-50 text-rose-700">{ev.gallery_videos.length} videos</span>}
                </div>
                <button
                  onClick={() => setGalleryEventId(ev.id)}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white text-xs font-semibold transition-all shadow-sm hover:shadow"
                  data-testid={`manage-gallery-${i}`}>
                  <Image size={14} /> Gérer la galerie photos / vidéos
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingEvent ? "Modifier l'événement" : 'Nouvel événement'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <CoverUpload value={form.poster_image} onChange={v => setForm(p => ({ ...p, poster_image: v }))} />
              <div><Label>Titre *</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required data-testid="event-title-input" /></div>
              <div><Label>Description *</Label><RichTextEditor value={form.description} onChange={val => setForm(p => ({ ...p, description: val }))} placeholder="Description..." height="120px" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Type</Label>
                  <select value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="event-type-select">
                    {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div><Label>Format</Label>
                  <select value={form.location_type} onChange={e => setForm(p => ({ ...p, location_type: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm">
                    <option value="physical">Presentiel</option><option value="online">En ligne</option><option value="hybrid">Hybride</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date debut *</Label><Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} required data-testid="event-start-date" /></div>
                <div><Label>Date fin</Label><Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Lieu</Label><Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Paris" /></div>
                <div><Label>Participants max</Label><Input type="number" value={form.max_participants} onChange={e => setForm(p => ({ ...p, max_participants: e.target.value }))} /></div>
              </div>
              {(form.location_type === 'online' || form.location_type === 'hybrid') && (
                <div><Label>Lien de connexion</Label><Input type="url" value={form.online_link} onChange={e => setForm(p => ({ ...p, online_link: e.target.value }))} placeholder="https://zoom.us/..." /></div>
              )}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.registration_open} onChange={e => setForm(p => ({ ...p, registration_open: e.target.checked }))} id="reg-open" className="rounded" />
                  <label htmlFor="reg-open" className="text-sm text-gray-700">Inscriptions ouvertes</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} id="ev-pub" className="rounded" />
                  <label htmlFor="ev-pub" className="text-sm text-gray-700">Publier</label>
                </div>
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="submit-event-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                {editingEvent ? 'Mettre à jour' : "Créer l'événement"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Gallery Dialog */}
        <Dialog open={!!galleryEventId} onOpenChange={() => setGalleryEventId(null)}>
          <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Galerie : {galleryEvent?.title}</DialogTitle></DialogHeader>
            {galleryEvent && (
              <GalleryManager
                eventId={galleryEvent.id}
                photos={galleryEvent.gallery_photos || []}
                videos={galleryEvent.gallery_videos || []}
                recapVideoUrl={galleryEvent.recap_video_url || ''}
                onUpdate={(p, v, r) => {
                  setEvents(prev => prev.map(e => e.id === galleryEventId ? { ...e, gallery_photos: p, gallery_videos: v, recap_video_url: r } : e));
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default EventsManagementPage;
